import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { TAPSocketReservedEvents, } from '../types';
import { io } from 'socket.io-client';
import { MultipleSocketSubscriptionException } from '../exceptions';
import { logger, roomIdsGenerator, topicSubscriptionPayloadGenerator } from '../utils';
export const SocketNotificationContext = createContext({
    subscribeTopic: () => { },
    unsubscribeTopic: () => { },
});
export const SocketNotificationProvider = ({ children, allowMultipleSameTopicSubscriptions, path, baseUrl, options = {}, onConnectionError, }) => {
    const subscriptionDictionary = useRef({});
    const listenerDictionary = useRef({});
    const [socket] = useState(() => {
        const socketInstance = io(baseUrl, { ...options, path });
        socketInstance.on(TAPSocketReservedEvents.DISCONNECT, (reason) => {
            if (reason === 'io server disconnect') {
                socketInstance.connect();
                reSubscribeTopics(socketInstance);
            }
        });
        socketInstance.io.on(TAPSocketReservedEvents.RECONNECT, () => {
            reSubscribeTopics(socketInstance);
        });
        onConnectionError && socketInstance.on(TAPSocketReservedEvents.CONNECT_ERROR, onConnectionError);
        return socketInstance;
    });
    const reSubscribeTopics = (socketInstance) => {
        const roomIds = Object.keys(subscriptionDictionary.current);
        topicSubscriptionPayloadGenerator(roomIds).forEach((payload) => {
            socketInstance.emit(TAPSocketReservedEvents.SUBSCRIBE_TOPICS, payload);
        });
    };
    useEffect(() => {
        return () => {
            socket.connected && socket.disconnect();
        };
    }, [socket]);
    const subscribeTopic = useCallback(({ topic, recipients, subscriberId, onMessage }) => {
        const roomIds = roomIdsGenerator(topic, recipients);
        if (!allowMultipleSameTopicSubscriptions) {
            roomIds.forEach((roomId) => {
                if (subscriptionDictionary.current[roomId]) {
                    throw new MultipleSocketSubscriptionException(roomId);
                }
            });
        }
        if (listenerDictionary.current[subscriberId]) {
            socket.off(topic, listenerDictionary.current[subscriberId]);
        }
        listenerDictionary.current[subscriberId] = onMessage;
        socket.on(topic, onMessage);
        socket.emit(TAPSocketReservedEvents.SUBSCRIBE_TOPICS, {
            topics: [topic],
            subscribers: recipients,
        });
        logger.debug('emitted topic subscription event', topic, recipients);
        roomIds.forEach((roomId) => {
            var _a;
            subscriptionDictionary.current[roomId] = ((_a = subscriptionDictionary.current[roomId]) !== null && _a !== void 0 ? _a : 0) + 1;
        });
    }, [allowMultipleSameTopicSubscriptions, socket]);
    const unsubscribeTopic = useCallback(({ topic, recipients, subscriberId }) => {
        socket.off(topic, listenerDictionary.current[subscriberId]);
        delete listenerDictionary.current[subscriberId];
        const roomIds = roomIdsGenerator(topic, recipients);
        let isTopicStillSubscribed = false;
        roomIds.forEach((roomId) => {
            subscriptionDictionary.current[roomId]--;
            if (subscriptionDictionary.current[roomId] > 0) {
                isTopicStillSubscribed = true;
            }
            else {
                delete subscriptionDictionary.current[roomId];
            }
        });
        !isTopicStillSubscribed &&
            socket.emit(TAPSocketReservedEvents.UNSUBSCRIBE_TOPICS, {
                topics: [topic],
                subscribers: recipients,
            });
    }, [allowMultipleSameTopicSubscriptions, socket]);
    return (React.createElement(SocketNotificationContext.Provider, { value: { subscribeTopic, unsubscribeTopic } }, children));
};
export const useSocketNotificationContext = () => useContext(SocketNotificationContext);
//# sourceMappingURL=SocketNotificationContext.js.map